import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Dropdown,
  Icon,
  Label,
  Message,
} from "semantic-ui-react";
import { useStore } from "../../app/stores/store";
import { useTranslation } from "react-i18next";
import { TournamentDto, TournamentMatchDto } from "../../app/models/Tournament";
import TournamentMatchModal from "./TournamentMatchModal";
import { format } from "date-fns";

interface Props {
  tournament: TournamentDto;
}

const CARD_W = 190;
const CARD_H = 78;
const ADV_COL_W = 172;
const ADV_COL_H = 36;
const H_GAP = 14;
const V_GAP = 80;
const SVG_PAD = 16;

const STRIDE = CARD_W + ADV_COL_W + H_GAP;

interface RoundMap {
  keys: number[];
  data: Record<number, TournamentMatchDto[]>;
}

interface BracketData {
  rounds: RoundMap;
  positions: Record<string, { x: number; y: number }>;
  svgW: number;
  svgH: number;
  connectors: React.ReactNode[];
}

function groupByRound(
  matches: TournamentMatchDto[],
  side: "Winners" | "Losers" | "Grand" | "ThirdPlace",
): RoundMap {
  const data: Record<number, TournamentMatchDto[]> = {};
  matches
    .filter((m) => m.bracketSide === side)
    .forEach((m) => {
      if (!data[m.round]) data[m.round] = [];
      data[m.round].push(m);
    });
  const keys = Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b);
  keys.forEach((k) => {
    data[k] = data[k].slice().sort((a, b) => a.matchNumber - b.matchNumber);
  });
  return { keys, data };
}

function flatMatches(rounds: RoundMap): TournamentMatchDto[] {
  return rounds.keys.reduce<TournamentMatchDto[]>(
    (acc, k) => acc.concat(rounds.data[k] ?? []),
    [],
  );
}

function matchColor(status: string): string {
  if (status === "Confirmed") return "#21ba45";
  if (status === "Disputed") return "#db2828";
  if (status === "Reported") return "#f2711c";
  if (status === "Walkover") return "#767676";
  return "#2185d0";
}

function isFinished(status: string): boolean {
  return (
    status === "Confirmed" || status === "Resolved" || status === "Walkover"
  );
}

function getWinnerId(match: TournamentMatchDto): string | null {
  if (match.status === "Walkover" && match.walkoverWinnerId)
    return match.walkoverWinnerId;
  if (match.result) {
    if (match.result.scoreA > match.result.scoreB) return match.teamAId ?? null;
    if (match.result.scoreB > match.result.scoreA) return match.teamBId ?? null;
  }
  return null;
}

function buildBracket(
  matches: TournamentMatchDto[],
  side: "Winners" | "Losers" | "Grand",
  offsetY: number,
): BracketData {
  const rounds = groupByRound(matches, side);
  const positions: Record<string, { x: number; y: number }> = {};

  const firstRoundCount =
    rounds.keys.length > 0 ? (rounds.data[rounds.keys[0]]?.length ?? 1) : 1;

  rounds.keys.forEach((round, ri) => {
    const roundMatches = rounds.data[round] ?? [];
    const slotH = (CARD_H + V_GAP) * Math.pow(2, ri);
    const x = ri * STRIDE;
    roundMatches.forEach((m, mi) => {
      const y = offsetY + mi * slotH + (slotH - CARD_H) / 2;
      positions[m.id] = { x, y };
    });
  });

  const svgW = rounds.keys.length * STRIDE;
  const svgH = offsetY + firstRoundCount * (CARD_H + V_GAP) + V_GAP;

  const connectors: React.ReactNode[] = [];
  rounds.keys.forEach((round, ri) => {
    if (ri === 0) return;
    const prevMatches = rounds.data[rounds.keys[ri - 1]] ?? [];
    const currMatches = rounds.data[round] ?? [];

    currMatches.forEach((curr, ci) => {
      const pos = positions[curr.id];
      if (!pos) return;
      const feeders = prevMatches.slice(ci * 2, ci * 2 + 2);
      feeders.forEach((feeder) => {
        const fPos = positions[feeder.id];
        if (!fPos) return;
        const mx = fPos.x + CARD_W + ADV_COL_W + H_GAP / 2;
        connectors.push(
          <path
            key={`conn-${feeder.id}-${curr.id}`}
            d={`M${fPos.x + CARD_W},${fPos.y + CARD_H / 2} H${mx} V${pos.y + CARD_H / 2} H${pos.x}`}
            fill="none"
            stroke="#d0d5e0"
            strokeWidth={1.5}
            strokeLinecap="round"
          />,
        );
      });
    });
  });

  return { rounds, positions, svgW, svgH, connectors };
}

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

interface MatchCardProps {
  match: TournamentMatchDto;
  x: number;
  y: number;
  onClick: () => void;
}

function MatchCard({ match, x, y, onClick }: MatchCardProps) {
  const [hovered, setHovered] = useState(false);

  const teamA = match.teamAName || (match.teamAId ? "?" : "TBD");
  const teamB = match.teamBName || (match.teamBId ? "?" : "TBD");
  const scoreA = match.result?.scoreA;
  const scoreB = match.result?.scoreB;
  const border = matchColor(match.status);
  const winnerA =
    scoreA !== undefined && scoreB !== undefined && scoreA > scoreB;
  const winnerB =
    scoreA !== undefined && scoreB !== undefined && scoreB > scoreA;
  const isTbd = (name: string) => name === "TBD" || name === "BYE";

  return (
    <g
      transform={`translate(${x},${y})`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ cursor: "pointer" }}
    >
      <rect
        x={2}
        y={2}
        width={CARD_W}
        height={CARD_H}
        rx={7}
        fill="rgba(0,0,0,0.06)"
      />
      <rect
        x={0}
        y={0}
        width={CARD_W}
        height={CARD_H}
        rx={7}
        fill={hovered ? "#f4f7ff" : "#ffffff"}
        stroke={border}
        strokeWidth={hovered ? 2 : 1.5}
      />
      <rect x={0} y={0} width={4} height={CARD_H} rx={3} fill={border} />
      <line
        x1={4}
        y1={34}
        x2={CARD_W}
        y2={34}
        stroke="#eff0f3"
        strokeWidth={1}
      />
      <text
        x={13}
        y={22}
        fontSize={11}
        fontWeight={winnerA ? 700 : 400}
        fill={isTbd(teamA) ? "#c0c4cc" : winnerA ? "#21ba45" : "#1a1d23"}
        dominantBaseline="middle"
      >
        {teamA.length > 20 ? teamA.substring(0, 19) + "…" : teamA}
      </text>
      {scoreA !== undefined && (
        <text
          x={CARD_W - 10}
          y={22}
          fontSize={12}
          fontWeight={700}
          fill={winnerA ? "#21ba45" : "#6b7280"}
          textAnchor="end"
          dominantBaseline="middle"
        >
          {scoreA}
        </text>
      )}
      <text
        x={13}
        y={46}
        fontSize={11}
        fontWeight={winnerB ? 700 : 400}
        fill={isTbd(teamB) ? "#c0c4cc" : winnerB ? "#21ba45" : "#1a1d23"}
        dominantBaseline="middle"
      >
        {teamB.length > 20 ? teamB.substring(0, 19) + "…" : teamB}
      </text>
      {scoreB !== undefined && (
        <text
          x={CARD_W - 10}
          y={46}
          fontSize={12}
          fontWeight={700}
          fill={winnerB ? "#21ba45" : "#6b7280"}
          textAnchor="end"
          dominantBaseline="middle"
        >
          {scoreB}
        </text>
      )}
      {match.scheduledAt && (
        <>
          <line
            x1={4}
            y1={58}
            x2={CARD_W}
            y2={58}
            stroke="#eff0f3"
            strokeWidth={1}
          />
          <text
            x={13}
            y={68}
            fontSize={9}
            fill="#9ca3af"
            dominantBaseline="middle"
          >
            {format(new Date(match.scheduledAt), "dd MMM, HH:mm")}
          </text>
        </>
      )}
      {match.status === "Walkover" && (
        <text
          x={CARD_W - 7}
          y={9}
          fontSize={8}
          fill="#767676"
          textAnchor="end"
          fontWeight={600}
        >
          WO
        </text>
      )}
    </g>
  );
}

// ── HTML Advance Overlay (replaces foreignObject-based AdvanceControl) ────────
// Rendered as a normal HTML div absolutely positioned over the SVG.
// This avoids the well-known browser bug where foreignObject content inside SVG
// does not reliably re-render when React updates state / props.

interface HtmlAdvanceItemProps {
  match: TournamentMatchDto;
  // pixel coords relative to the scrollable SVG container (incl. SVG_PAD offset)
  left: number;
  top: number;
  onAdvance: (matchId: string, winnerId: string) => void;
  submitting: boolean;
}

function HtmlAdvanceItem({
  match,
  left,
  top,
  onAdvance,
  submitting,
}: HtmlAdvanceItemProps) {
  const { t } = useTranslation();
  const isByeMatch = !!match.teamAId !== !!match.teamBId;
  const autoWinner = getWinnerId(match)
    ?? (isByeMatch ? (match.teamAId ?? match.teamBId ?? "") : "");
  const [selected, setSelected] = useState<string>(autoWinner);

  useEffect(() => {
    setSelected(autoWinner);
  }, [autoWinner]);

  const options = [
    match.teamAId
      ? { key: match.teamAId, value: match.teamAId, text: match.teamAName ?? "Team A" }
      : null,
    match.teamBId
      ? { key: match.teamBId, value: match.teamBId, text: match.teamBName ?? "Team B" }
      : null,
  ].filter(Boolean) as { key: string; value: string; text: string }[];

  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: ADV_COL_W - 10,
        height: ADV_COL_H,
        display: "flex",
        gap: 3,
        alignItems: "center",
        background: isByeMatch ? "#fff8e1" : "#eef4fd",
        border: `1px solid ${isByeMatch ? "#ffe082" : "#c5d8f0"}`,
        borderRadius: 6,
        padding: "0 4px",
        boxSizing: "border-box",
        zIndex: 10,
      }}
    >
      <Dropdown
        selection
        compact
        options={options}
        value={selected}
        onChange={(_, d) => setSelected(d.value as string)}
        upward={false}
        style={{ fontSize: "0.72em", flex: 1, minWidth: 0 }}
        closeOnChange
        selectOnBlur={false}
      />
      <Button
        size="mini"
        color="teal"
        loading={submitting}
        disabled={!selected || submitting}
        onClick={() => onAdvance(match.id, selected)}
        title={t("tournament.bracket.advance")}
        style={{ fontSize: "0.7em", padding: "5px 7px", flexShrink: 0 }}
      >
        <Icon name="arrow right" style={{ margin: 0 }} />
      </Button>
    </div>
  );
}

interface HtmlAdvanceOverlayProps {
  rounds: RoundMap;
  positions: Record<string, { x: number; y: number }>;
  onAdvance: (matchId: string, winnerId: string) => void;
  submitting: boolean;
  isLastBracket?: boolean;
  hasGrandFinal?: boolean;
}

function HtmlAdvanceOverlay({
  rounds,
  positions,
  onAdvance,
  submitting,
  isLastBracket = false,
  hasGrandFinal = false,
}: HtmlAdvanceOverlayProps) {
  const items: React.ReactNode[] = [];

  rounds.keys.forEach((round, ri) => {
    const isLastRound = ri === rounds.keys.length - 1;
    // Skip last round unless it feeds into a Grand Final
    if (isLastRound && !isLastBracket && !hasGrandFinal) return;

    (rounds.data[round] ?? []).forEach((m) => {
      const isByeMatch = !!m.teamAId !== !!m.teamBId;
      const isEmptyMatch = !m.teamAId && !m.teamBId;
      if ((!isFinished(m.status) && !isByeMatch) || isEmptyMatch) return;

      const pos = positions[m.id];
      if (!pos) return;

      // Position the HTML div to align with the card's right edge + gap
      const left = pos.x + CARD_W + 6 + SVG_PAD;
      const top  = pos.y + (CARD_H - ADV_COL_H) / 2 + SVG_PAD;

      items.push(
        <HtmlAdvanceItem
          key={`adv-${m.id}-${m.status}-${m.teamAId ?? "x"}-${m.teamBId ?? "x"}`}
          match={m}
          left={left}
          top={top}
          onAdvance={onAdvance}
          submitting={submitting}
        />,
      );
    });
  });

  return <>{items}</>;
}

interface MobileMatchListProps {
  rounds: RoundMap;
  isAdmin: boolean;
  hasGrandFinal?: boolean;
  onMatchClick: (m: TournamentMatchDto) => void;
  onAdvance: (matchId: string, winnerId: string) => void;
  submitting: boolean;
}

function MobileMatchList({
  rounds,
  isAdmin,
  hasGrandFinal = false,
  onMatchClick,
  onAdvance,
  submitting,
}: MobileMatchListProps) {
  const { t } = useTranslation();

  return (
    <div>
      {rounds.keys.map((round, ri) => {
        const isLastRound = ri === rounds.keys.length - 1;
        const roundMatches = rounds.data[round] ?? [];
        const roundLabel = isLastRound
          ? t("tournament.bracket.final")
          : `${t("tournament.bracket.round")} ${round}`;

        return (
          <div key={round} style={{ marginBottom: 20 }}>
            <div
              style={{
                fontSize: "0.72em",
                fontWeight: 700,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: isLastRound ? "#b8860b" : "#6b7280",
                marginBottom: 8,
                paddingLeft: 2,
              }}
            >
              {roundLabel}
              <span
                style={{
                  marginLeft: 6,
                  background: isLastRound ? "#fef3c7" : "#f3f4f6",
                  color: isLastRound ? "#92400e" : "#6b7280",
                  borderRadius: 10,
                  padding: "1px 7px",
                  fontSize: "0.9em",
                }}
              >
                {roundMatches.length}
              </span>
            </div>

            {roundMatches.map((m) => (
              <MobileMatchCard
                key={`${m.id}-${m.status}-${m.teamAId ?? "x"}-${m.teamBId ?? "x"}`}
                match={m}
                isAdmin={isAdmin}
                isLastRound={isLastRound}
                hasGrandFinal={isLastRound ? hasGrandFinal : false}
                onMatchClick={onMatchClick}
                onAdvance={onAdvance}
                submitting={submitting}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

interface MobileMatchCardProps {
  match: TournamentMatchDto;
  isAdmin: boolean;
  isLastRound: boolean;
  hasGrandFinal?: boolean;
  isThirdPlace?: boolean;
  onMatchClick: (m: TournamentMatchDto) => void;
  onAdvance: (matchId: string, winnerId: string) => void;
  submitting: boolean;
}

function MobileMatchCard({
  match,
  isAdmin,
  isLastRound,
  hasGrandFinal = false,
  isThirdPlace = false,
  onMatchClick,
  onAdvance,
  submitting,
}: MobileMatchCardProps) {
  const { t } = useTranslation();
  const autoWinner = getWinnerId(match);
  const [selected, setSelected] = useState<string>(autoWinner ?? "");

  useEffect(() => {
    setSelected(autoWinner ?? "");
  }, [autoWinner]);

  const border = matchColor(match.status);
  const scoreA = match.result?.scoreA;
  const scoreB = match.result?.scoreB;
  const winnerA =
    scoreA !== undefined && scoreB !== undefined && scoreA > scoreB;
  const winnerB =
    scoreA !== undefined && scoreB !== undefined && scoreB > scoreA;
  const isByeMatch = !!match.teamAId !== !!match.teamBId;

  // Show advance control when:
  // - match is finished (or bye), AND
  // - there IS a next destination (not the last round, OR it's last but feeds into Grand Final)
  // - third place match never advances anyone further
  const canAdvance = !isThirdPlace && (!isLastRound || hasGrandFinal);
  const showAdvance =
    isAdmin &&
    (isFinished(match.status) || isByeMatch) &&
    canAdvance &&
    (!!match.teamAId || !!match.teamBId);

  const advanceOptions = [
    match.teamAId
      ? {
          key: match.teamAId,
          value: match.teamAId,
          text: match.teamAName ?? "Team A",
        }
      : null,
    match.teamBId
      ? {
          key: match.teamBId,
          value: match.teamBId,
          text: match.teamBName ?? "Team B",
        }
      : null,
  ].filter(Boolean) as { key: string; value: string; text: string }[];

  return (
    <div
      style={{
        borderRadius: 10,
        border: `1.5px solid ${border}`,
        background: "#fff",
        marginBottom: 10,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div
        onClick={() => onMatchClick(match)}
        style={{ cursor: "pointer", padding: "10px 12px" }}
      >
        <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
          <div
            style={{
              width: 4,
              borderRadius: 3,
              flexShrink: 0,
              background: border,
              alignSelf: "stretch",
              minHeight: 44,
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 6,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <span
                style={{
                  fontSize: "0.88em",
                  fontWeight: winnerA ? 700 : 400,
                  color: !match.teamAId
                    ? "#c0c4cc"
                    : winnerA
                      ? "#21ba45"
                      : "#1a1d23",
                }}
              >
                {match.teamAName || "TBD"}
              </span>
              {scoreA !== undefined && (
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "1em",
                    color: winnerA ? "#21ba45" : "#6b7280",
                    minWidth: 20,
                    textAlign: "right",
                  }}
                >
                  {scoreA}
                </span>
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: 6,
              }}
            >
              <span
                style={{
                  fontSize: "0.88em",
                  fontWeight: winnerB ? 700 : 400,
                  color: !match.teamBId
                    ? "#c0c4cc"
                    : winnerB
                      ? "#21ba45"
                      : "#1a1d23",
                }}
              >
                {match.teamBName || "TBD"}
              </span>
              {scoreB !== undefined && (
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "1em",
                    color: winnerB ? "#21ba45" : "#6b7280",
                    minWidth: 20,
                    textAlign: "right",
                  }}
                >
                  {scoreB}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Label
              size="mini"
              style={{
                background: border,
                color: "#fff",
                border: "none",
                fontSize: "0.68em",
              }}
            >
              {t(`tournament.matchStatus.${match.status.toLowerCase()}`)}
            </Label>
          </div>
        </div>

        {match.scheduledAt && (
          <div
            style={{
              fontSize: "0.74em",
              color: "#9ca3af",
              marginTop: 6,
              paddingLeft: 14,
            }}
          >
            <Icon
              name="calendar alternate outline"
              style={{ marginRight: 3 }}
            />
            {format(new Date(match.scheduledAt), "dd MMM yyyy, HH:mm")}
          </div>
        )}
      </div>

      {showAdvance && (
        <div
          style={{
            borderTop: "1px dashed #e5e7eb",
            padding: "8px 12px",
            background: isByeMatch ? "#fffbf0" : "#f8faff",
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{ fontSize: "0.74em", color: "#6b7280", fontWeight: 600 }}
          >
            <Icon name="arrow right" color="teal" />
            {t("tournament.bracket.advanceWinner")}:
          </span>
          <Dropdown
            selection
            compact
            options={advanceOptions}
            value={selected}
            onChange={(_, d) => setSelected(d.value as string)}
            style={{ fontSize: "0.8em", minWidth: 120 }}
          />
          <Button
            size="mini"
            color="teal"
            loading={submitting}
            disabled={!selected || submitting}
            onClick={() => onAdvance(match.id, selected)}
          >
            {t("tournament.bracket.advance")}
          </Button>
        </div>
      )}
    </div>
  );
}

interface PrintContentProps {
  tournament: TournamentDto;
  rounds: RoundMap;
}

const PrintContent = React.forwardRef<HTMLDivElement, PrintContentProps>(
  ({ tournament, rounds }, ref) => {
    const { t } = useTranslation();
    return (
      <div ref={ref} style={{ display: "none" }}>
        <h1>{tournament.name} — Bracket</h1>
        <p>
          {t(`tournament.format.${tournament.format.toLowerCase()}`)}
          {" | "}
          {new Date(tournament.startsAt).toLocaleDateString()}
        </p>
        {rounds.keys.map((round, i) => {
          const rMatches = rounds.data[round] ?? [];
          const isLast = i === rounds.keys.length - 1;
          return (
            <div key={round}>
              <div className="section-title">
                {isLast
                  ? t("tournament.bracket.final")
                  : `${t("tournament.bracket.round")} ${round}`}
              </div>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("tournament.bracket.teamA")}</th>
                    <th>{t("tournament.bracket.score")}</th>
                    <th>{t("tournament.bracket.teamB")}</th>
                    <th>{t("tournament.bracket.scheduledAt")}</th>
                    <th>{t("tournament.bracket.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rMatches.map((m) => (
                    <tr key={m.id}>
                      <td>{m.matchNumber}</td>
                      <td>{m.teamAName || "TBD"}</td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        {m.result
                          ? `${m.result.scoreA} : ${m.result.scoreB}`
                          : "- : -"}
                      </td>
                      <td>{m.teamBName || "TBD"}</td>
                      <td>
                        {m.scheduledAt
                          ? format(
                              new Date(m.scheduledAt),
                              "dd.MM.yyyy HH:mm",
                            )
                          : "-"}
                      </td>
                      <td>{m.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  },
);
PrintContent.displayName = "PrintContent";

export default observer(function TournamentBracketView({ tournament }: Props) {
  const { tournamentStore } = useStore();
  const { t } = useTranslation();
  const [selectedMatch, setSelectedMatch] = useState<TournamentMatchDto | null>(
    null,
  );
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // tournamentStore.tournamentMatches is a MobX observable array.
  // Calling .slice() creates a new plain JS array every render when MobX
  // triggers the observer, which ensures useMemo dependency comparison works.
  const matches = tournamentStore.tournamentMatches.slice();
  const isAdmin =
    tournament.isCurrentUserOrganizer || tournament.isCurrentUserCoOrganizer;
  const isDouble = tournament.format === "DoubleElimination";

  // Build a stable string key that changes whenever any match data changes.
  // Used as the single dependency for all derived data so everything stays in sync.
  const matchesKey = matches
    .map((m) => `${m.id}-${m.status}-${m.teamAId ?? ""}-${m.teamBId ?? ""}-${m.result?.scoreA ?? ""}-${m.result?.scoreB ?? ""}-${m.scheduledAt ?? ""}`)
    .join("|");

  const winnerMatches = useMemo(
    () =>
      isDouble ? matches.filter((m) => m.bracketSide === "Winners") : matches.filter((m) => m.bracketSide === "Winners"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchesKey, isDouble],
  );
  const loserMatches = useMemo(
    () => matches.filter((m) => m.bracketSide === "Losers"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchesKey],
  );
  const grandMatches = useMemo(
    () => matches.filter((m) => m.bracketSide === "Grand"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchesKey],
  );

  // Third place matches — Single Elimination only
  const thirdPlaceMatches = useMemo(
    () => matches.filter((m) => m.bracketSide === "ThirdPlace"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchesKey],
  );

  const wb = useMemo(
    () => buildBracket(winnerMatches, "Winners", 0),
    [winnerMatches],
  );
  const lb = useMemo(
    () => buildBracket(loserMatches, "Losers", wb.svgH + 40),
    [loserMatches, wb.svgH],
  );

  const grandOffset = isDouble ? lb.svgH + 40 : wb.svgH + 40;

  // Third place section sits below the winners bracket with a small gap
  const thirdPlaceOffset = wb.svgH + 20;

  const totalH = (() => {
    if (grandMatches.length > 0) return grandOffset + CARD_H + 80;
    if (isDouble) return lb.svgH;
    if (thirdPlaceMatches.length > 0) return thirdPlaceOffset + 60 + CARD_H + 40;
    return wb.svgH;
  })();

  const totalW = Math.max(
    wb.svgW,
    isDouble ? lb.svgW : 0,
    grandMatches.length > 0 ? CARD_W + 80 : 0,
  );

  const mobileWinnerRounds = useMemo(
    () => groupByRound(winnerMatches, "Winners"),
    [winnerMatches],
  );
  const mobileLoserRounds = useMemo(
    () => (isDouble ? groupByRound(loserMatches, "Losers") : null),
    [isDouble, loserMatches],
  );

  const handleAdvance = useCallback(
    (matchId: string, winnerId: string) =>
      tournamentStore.advanceWinner(matchId, winnerId),
    [tournamentStore],
  );

  // When store reloads matches (after schedule update, result report, etc.),
  // sync the locally-selected match so the modal receives fresh data.
  useEffect(() => {
    if (!selectedMatch) return;
    const fresh = tournamentStore.tournamentMatches.find(m => m.id === selectedMatch.id);
    if (fresh) setSelectedMatch(fresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesKey]);

  const handlePrint = useCallback(() => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    const content = printRef.current?.innerHTML ?? "";
    printWindow.document.write(`
      <html><head><title>${tournament.name} - Bracket</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px}
        h1{font-size:18px;margin-bottom:8px}
        p{color:#666;font-size:12px;margin-bottom:16px}
        table{border-collapse:collapse;width:100%;margin-bottom:24px}
        th{background:#2185d0;color:white;padding:6px 10px;font-size:12px;text-align:left}
        td{border:1px solid #ddd;padding:6px 10px;font-size:11px}
        tr:nth-child(even){background:#f9f9f9}
        .section-title{font-size:14px;font-weight:bold;margin:16px 0 6px;color:#333}
      </style>
      </head><body>${content}</body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [tournament.name]);

  if (matches.length === 0) {
    return (
      <Message info>
        <Icon name="magic" />
        {isAdmin
          ? t("tournament.bracket.notGenerated")
          : t("tournament.bracket.waitingForBracket")}
      </Message>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {wb.rounds.keys.map((r, i) => {
            const isLast = i === wb.rounds.keys.length - 1;
            return (
              <Label
                key={r}
                size="tiny"
                color={isLast ? "yellow" : "grey"}
                basic
              >
                {isLast
                  ? t("tournament.bracket.final")
                  : `${t("tournament.bracket.round")} ${r}`}
                <span style={{ marginLeft: 4, color: "#888" }}>
                  ({wb.rounds.data[r]?.length ?? 0})
                </span>
              </Label>
            );
          })}
          {thirdPlaceMatches.length > 0 && (
            <Label size="tiny" color="orange" basic>
              🥉 {t("tournament.bracket.thirdPlace")}
            </Label>
          )}
        </div>
        {!isMobile && (
          <Button
            size="small"
            basic
            icon="print"
            content={t("tournament.bracket.print")}
            onClick={handlePrint}
          />
        )}
      </div>

      {isAdmin && (
        <Message info size="small" style={{ marginBottom: 10 }}>
          <Icon name="info circle" />
          {t("tournament.bracket.adminAdvanceHint")}
        </Message>
      )}

      <PrintContent ref={printRef} tournament={tournament} rounds={wb.rounds} />

      {isMobile && (
        <div>
          {isDouble && (
            <div
              style={{
                fontSize: "0.78em",
                fontWeight: 700,
                color: "#2185d0",
                marginBottom: 6,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              {t("tournament.bracket.winners")}
            </div>
          )}
          <MobileMatchList
            rounds={mobileWinnerRounds}
            isAdmin={isAdmin}
            hasGrandFinal={grandMatches.length > 0 || !isDouble}  
            onMatchClick={setSelectedMatch}
            onAdvance={handleAdvance}
            submitting={tournamentStore.submitting}
          />

          {isDouble && mobileLoserRounds && (
            <>
              <div
                style={{
                  fontSize: "0.78em",
                  fontWeight: 700,
                  color: "#e03997",
                  margin: "16px 0 6px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                {t("tournament.bracket.losers")}
              </div>
              <MobileMatchList
                rounds={mobileLoserRounds}
                isAdmin={isAdmin}
                hasGrandFinal={grandMatches.length > 0}
                onMatchClick={setSelectedMatch}
                onAdvance={handleAdvance}
                submitting={tournamentStore.submitting}
              />
            </>
          )}

          {grandMatches.length > 0 && (
            <>
              <div
                style={{
                  fontSize: "0.78em",
                  fontWeight: 700,
                  color: "#f2711c",
                  margin: "16px 0 6px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                {t("tournament.bracket.grandFinal")}
              </div>
              {grandMatches.map((m) => (
                <MobileMatchCard
                  key={`${m.id}-${m.status}-${m.teamAId ?? "x"}-${m.teamBId ?? "x"}`}
                  match={m}
                  isAdmin={isAdmin}
                  isLastRound={true}
                  onMatchClick={setSelectedMatch}
                  onAdvance={handleAdvance}
                  submitting={tournamentStore.submitting}
                />
              ))}
            </>
          )}

          {/* Third place match — mobile, Single Elimination only */}
          {!isDouble && thirdPlaceMatches.length > 0 && (
            <>
              <div
                style={{
                  fontSize: "0.78em",
                  fontWeight: 700,
                  color: "#cd853f",
                  margin: "16px 0 6px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                🥉 {t("tournament.bracket.thirdPlace")}
              </div>
              {thirdPlaceMatches.map((m) => (
                <MobileMatchCard
                  key={`${m.id}-${m.status}-${m.teamAId ?? "x"}-${m.teamBId ?? "x"}`}
                  match={m}
                  isAdmin={isAdmin}
                  isLastRound={true}
                  isThirdPlace={true}
                  onMatchClick={setSelectedMatch}
                  onAdvance={handleAdvance}
                  submitting={tournamentStore.submitting}
                />
              ))}
            </>
          )}
        </div>
      )}

      {!isMobile && (
        <div
          style={{
            position: "relative", // needed so HTML advance overlay can position against this
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "72vh",
            border: "1px solid #e8eaed",
            borderRadius: 10,
            background: "#f8f9fb",
            padding: SVG_PAD,
          }}
        >
          <svg
            key={matchesKey}
            width={totalW + SVG_PAD * 2}
            height={totalH + SVG_PAD * 2}
            style={{ display: "block", overflow: "visible" }}
          >
            {isDouble && (
              <text
                x={0}
                y={13}
                fontSize={11}
                fontWeight={700}
                fill="#2185d0"
              >
                {t("tournament.bracket.winners")}
              </text>
            )}

            {wb.connectors}

            {flatMatches(wb.rounds).map((m) => {
              const pos = wb.positions[m.id];
              if (!pos) return null;
              return (
                <MatchCard
                  key={`mc-${m.id}-${m.status}-${m.result?.scoreA ?? ""}-${m.result?.scoreB ?? ""}`}
                  match={m}
                  x={pos.x}
                  y={pos.y}
                  onClick={() => setSelectedMatch(m)}
                />
              );
            })}

            {wb.rounds.keys.map((r, i) => {
              const isLast = i === wb.rounds.keys.length - 1;
              const x = i * STRIDE + CARD_W / 2;
              return (
                <text
                  key={r}
                  x={x}
                  y={wb.svgH}
                  fontSize={10}
                  textAnchor="middle"
                  fill="#b0b5c0"
                >
                  {isLast ? t("tournament.bracket.final") : `R${r}`}
                </text>
              );
            })}

            {/* Third place match — desktop SVG, Single Elimination only */}
            {!isDouble && thirdPlaceMatches.length > 0 && (
              <>
                <line
                  x1={0}
                  y1={thirdPlaceOffset - 8}
                  x2={totalW + SVG_PAD}
                  y2={thirdPlaceOffset - 8}
                  stroke="#e8eaed"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                />
                <text
                  x={0}
                  y={thirdPlaceOffset + 12}
                  fontSize={11}
                  fontWeight={700}
                  fill="#cd853f"
                >
                  🥉 {t("tournament.bracket.thirdPlace")}
                </text>
                {thirdPlaceMatches.map((m, i) => (
                  <MatchCard
                    key={`mc-3rd-${m.id}-${m.status}-${m.result?.scoreA ?? ""}-${m.result?.scoreB ?? ""}`}
                    match={m}
                    x={60}
                    y={thirdPlaceOffset + 30 + i * (CARD_H + V_GAP)}
                    onClick={() => setSelectedMatch(m)}
                  />
                ))}
              </>
            )}

            {isDouble && (
              <>
                <text
                  x={0}
                  y={wb.svgH + 18}
                  fontSize={11}
                  fontWeight={700}
                  fill="#e03997"
                >
                  {t("tournament.bracket.losers")}
                </text>
                {lb.connectors}
                {flatMatches(lb.rounds).map((m) => {
                  const pos = lb.positions[m.id];
                  if (!pos) return null;
                  return (
                    <MatchCard
                      key={`mc-${m.id}-${m.status}-${m.result?.scoreA ?? ""}-${m.result?.scoreB ?? ""}`}
                      match={m}
                      x={pos.x}
                      y={pos.y}
                      onClick={() => setSelectedMatch(m)}
                    />
                  );
                })}
              </>
            )}

            {grandMatches.length > 0 && (
              <>
                <text
                  x={0}
                  y={grandOffset - 10}
                  fontSize={11}
                  fontWeight={700}
                  fill="#f2711c"
                >
                  {t("tournament.bracket.grandFinal")}
                </text>
                {grandMatches.map((m, i) => (
                  <MatchCard
                    key={`mc-${m.id}-${m.status}-${m.result?.scoreA ?? ""}-${m.result?.scoreB ?? ""}`}
                    match={m}
                    x={60}
                    y={grandOffset + i * (CARD_H + V_GAP)}
                    onClick={() => setSelectedMatch(m)}
                  />
                ))}
              </>
            )}
          </svg>

          {/* HTML advance controls rendered OUTSIDE the SVG to avoid foreignObject re-render bugs */}
          {isAdmin && (
            <>
              <HtmlAdvanceOverlay
                rounds={wb.rounds}
                positions={wb.positions}
                onAdvance={handleAdvance}
                submitting={tournamentStore.submitting}
                isLastBracket={!isDouble} 
                hasGrandFinal={grandMatches.length > 0}
              />
              {isDouble && (
                <HtmlAdvanceOverlay
                  rounds={lb.rounds}
                  positions={lb.positions}
                  onAdvance={handleAdvance}
                  submitting={tournamentStore.submitting}
                  isLastBracket={true}
                  hasGrandFinal={grandMatches.length > 0}
                />
              )}
            </>
          )}
        </div>
      )}

      {selectedMatch && (
        <TournamentMatchModal
          match={selectedMatch}
          tournament={tournament}
          onClose={() => setSelectedMatch(null)}
        />
      )}
    </div>
  );
});